import {
  createContext,
  useState,
  ReactNode,
  FC,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { CurrencyType } from "../types/types";

interface CurrencyContextType {
  currency: CurrencyType;
  setCurrency: (currency: CurrencyType) => void;
}

export const CurrencyContext = createContext<CurrencyContextType | undefined>(
  undefined
);

interface CurrencyProviderProps {
  children: ReactNode;
}

const getUrlCurrency = (): CurrencyType | null => {
  if (typeof window === "undefined") return null;
  const param = new URLSearchParams(window.location.search).get("currency");
  return param === "BRL" || param === "USD" ? param : null;
};

export const CurrencyProvider: FC<CurrencyProviderProps> = ({ children }) => {
  // Track whether the user had a preference BEFORE mount so the locale
  // check can decide without being affected by the localStorage sync effect.
  const hadPreferenceAtMount = useRef(false);

  const [currency, setCurrency] = useState<CurrencyType>(() => {
    if (typeof window !== "undefined") {
      const storedCurrency = localStorage.getItem("preferred-currency");
      if (storedCurrency === "BRL" || storedCurrency === "USD") {
        hadPreferenceAtMount.current = true;
        return storedCurrency;
      }

      const urlCurrency = getUrlCurrency();
      if (urlCurrency) {
        hadPreferenceAtMount.current = true;
        return urlCurrency;
      }
    }
    return "USD";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncFromUrl = () => {
      const urlCurrency = getUrlCurrency();
      if (urlCurrency && urlCurrency !== currency) {
        setCurrency(urlCurrency);
      }
    };

    syncFromUrl();
    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, [currency]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("preferred-currency", currency);
    }
  }, [currency]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (hadPreferenceAtMount.current || getUrlCurrency()) return;

    const primaryLanguage = navigator.languages?.[0] ?? navigator.language;
    if (primaryLanguage?.startsWith("pt-BR")) {
      setCurrency("BRL");
    }
  }, []);

  const handleSetCurrency = useCallback((newCurrency: CurrencyType) => {
    setCurrency(newCurrency);

    if (typeof window !== "undefined" && window.history && window.location) {
      const url = new URL(window.location.href);
      url.searchParams.set("currency", newCurrency);
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  const value = {
    currency,
    setCurrency: handleSetCurrency,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
